import { Component, computed, inject, Inject, signal, Signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { CommonModule, DatePipe } from '@angular/common';
import { TranslateModule } from '@ngx-translate/core';
import { ChatMessageComponent, ChatMessageGroupComponent, GalaxyChatModule } from '@vendasta/galaxy/chat';
import { AiAssistantService, DEFAULT_AVATAR_SVG_ICON } from '@galaxy/ai-assistant';
import { map, Observable, of, shareReplay, startWith, switchMap, tap } from 'rxjs';
import { CallRecord, FunctionCall } from '@vendasta/protos/voice/v1alpha1';
import { getCallRecord$ } from '@vendasta/protos/voice/v1alpha1/voice-service';
import { Assistant } from '@vendasta/protos/ai_assistants/v1alpha1';
import { GalaxyPopoverModule, PopoverPositions } from '@vendasta/galaxy/popover';
import { MatIcon } from '@angular/material/icon';
import { MatIconButton } from '@angular/material/button';
import { ActivatedRoute } from '@angular/router';
import { CitationType } from '../citations/citations';
import { KeyValuePair } from '../../../../../../../core/src';
import { CitationsComponent } from '../citations/citations.component';
import { CallEndIndicatorComponent } from './call-end-indicator/call-end-indicator.component';

@Component({
  selector: 'inbox-call-transcript-modal',
  templateUrl: './call-transcript-modal.component.html',
  styleUrl: './call-transcript-modal.component.scss',
  imports: [
    MatDialogModule,
    TranslateModule,
    DatePipe,
    CommonModule,
    ChatMessageComponent,
    ChatMessageGroupComponent,
    GalaxyChatModule,
    GalaxyPopoverModule,
    MatIcon,
    MatIconButton,
    CitationsComponent,
    CallEndIndicatorComponent,
  ],
})
export class CallTranscriptModalComponent {
  transcript = this.buildTranscriptFromJSON(this.data.transcriptJSON);

  readonly OPENAI_BOT_SVG_ICON = DEFAULT_AVATAR_SVG_ICON;
  readonly OPENAI_BOT_BACKGROUND_COLOR = '#f6f6f6';

  private readonly assistantService = inject(AiAssistantService);

  callRecord$: Observable<CallRecord | undefined>;
  callRecordHasVoiceSettings$: Observable<boolean>;

  assistantThatHandledCall$: Observable<Partial<Assistant> | undefined>;
  assistantProfilePic$: Observable<string | undefined>;
  assistantLoading$: Observable<boolean>;

  show = signal(false);

  transcriptWithFunctionCalls: Signal<TranscriptMessageGroup[]>;

  route = inject(ActivatedRoute);
  showVoiceConfig$: Observable<boolean> = this.route.queryParamMap.pipe(
    tap((params) => console.log('query params map:', params)),
    map((params) => params.get('debug') === 'true'),
  );

  constructor(
    public dialogRef: MatDialogRef<CallTranscriptModalComponent>,
    // Is there a way to update this? fields of CallData could be Observables?
    @Inject(MAT_DIALOG_DATA) public data: CallData,
  ) {
    const namespace = data.accountGroupId
      ? {
          accountGroupNamespace: {
            accountGroupId: data.accountGroupId,
          },
        }
      : data.partnerId
        ? {
            partnerNamespace: {
              partnerId: data.partnerId,
            },
          }
        : undefined;
    if (data.callRecordId && namespace) {
      this.callRecord$ = getCallRecord$({
        id: data.callRecordId,
        namespace,
      }).pipe(
        map((resp) => resp.callRecord),
        shareReplay({ refCount: true, bufferSize: 1 }),
      );
    } else {
      this.callRecord$ = of(undefined);
    }

    this.callRecordHasVoiceSettings$ = this.callRecord$.pipe(
      map((callRecord) => {
        const config = callRecord?.metadata?.voiceConfig?.openaiRealtimeConfig;
        return Boolean(
          config?.voice ||
            config?.turnDetection?.silenceDuration ||
            config?.turnDetection?.prefixPadding ||
            config?.turnDetection?.threshold,
        );
      }),
    );

    const functionCalls = toSignal(
      this.callRecord$.pipe(map((callRecord) => callRecord?.metadata?.functionCalls ?? [])),
      { initialValue: [] as FunctionCall[] },
    );
    this.transcriptWithFunctionCalls = computed(() => this.attachFunctionCalls(this.transcript, functionCalls()));

    if (data.partnerId && data.accountGroupId) {
      const agid = data.accountGroupId;
      const pid = data.partnerId;
      this.assistantThatHandledCall$ = this.callRecord$.pipe(
        switchMap((callRecord) => {
          return this.assistantService.getAssistant(
            pid,
            agid,
            callRecord?.metadata?.assistantId || 'ASSISTANT-voice-receptionist',
          );
        }),
      );
    } else {
      this.assistantThatHandledCall$ = of(undefined);
    }

    const assistantDetails$ = this.assistantThatHandledCall$.pipe(
      map((assistant) => ({
        avatarUrl: assistant?.avatarUrl ?? undefined,
        loading: false,
      })),
      startWith({ avatarUrl: undefined, loading: true }),
    );
    this.assistantProfilePic$ = assistantDetails$.pipe(map((details) => details.avatarUrl));
    this.assistantLoading$ = assistantDetails$.pipe(map((details) => details.loading));
  }

  closeDialog() {
    this.dialogRef.close();
  }

  private attachFunctionCalls(
    groups: TranscriptMessageGroup[],
    functionCalls: FunctionCall[],
  ): TranscriptMessageGroup[] {
    const assignments = assignFunctionCallsToAssistantTurns(groups, functionCalls);

    return groups.map((group, groupIndex) => ({
      ...group,
      messages: group.messages.map((message, messageIndex) => ({
        ...message,
        functionCallsMetadata:
          group.name === 'To'
            ? functionCallsToCitations(assignments.get(`${groupIndex}:${messageIndex}`) ?? [])
            : undefined,
      })),
    }));
  }

  private buildTranscriptFromJSON(transcriptJSON: string): TranscriptMessageGroup[] {
    const messages: TranscriptMessage[] = JSON.parse(transcriptJSON);
    if (!messages || messages.length === 0) {
      return [];
    }
    const results: TranscriptMessageGroup[] = [
      {
        name: messages[0].name,
        messages: [messages[0]],
      },
    ];
    messages.shift();
    for (const message of messages) {
      if (message.name === results[results.length - 1].name) {
        results[results.length - 1].messages.push(message);
      } else {
        results.push({
          name: message.name,
          messages: [message],
        });
      }
    }
    return results;
  }

  protected readonly PopoverPositions = PopoverPositions;
}

interface CallData {
  partnerId: string;
  accountGroupId?: string;
  callRecordId: string | undefined;
  transcriptJSON: string;
  toName: string;
  fromName: string;
}

interface TranscriptMessage {
  content: string;
  timestamp: string;
  name: string;
  functionCallsMetadata?: KeyValuePair[];
}

export interface TranscriptMessageGroup {
  name: string;
  messages: TranscriptMessage[];
}

export function assignFunctionCallsToAssistantTurns(
  groups: TranscriptMessageGroup[],
  functionCalls: FunctionCall[],
): Map<string, FunctionCall[]> {
  const assignments = new Map<string, FunctionCall[]>();

  const assistantTurns: { key: string; time: number }[] = [];
  groups.forEach((group, groupIndex) => {
    if (group.name !== 'To') return;
    group.messages.forEach((message, messageIndex) => {
      assistantTurns.push({ key: `${groupIndex}:${messageIndex}`, time: new Date(message.timestamp).getTime() });
    });
  });

  if (assistantTurns.length === 0) return assignments;

  const lastTurn = assistantTurns[assistantTurns.length - 1];
  for (const functionCall of functionCalls) {
    const callTime = functionCall.calledAt ? new Date(functionCall.calledAt).getTime() : NaN;
    // the assistant speaks using the result after the tool runs, so anchor to the first turn at/after the call
    const target = isNaN(callTime) ? lastTurn : (assistantTurns.find((turn) => turn.time >= callTime) ?? lastTurn);
    const existing = assignments.get(target.key) ?? [];
    existing.push(functionCall);
    assignments.set(target.key, existing);
  }

  return assignments;
}

const CITATION_CONTENT_SIZE_LIMIT = 10 * 1024 * 1024;

export function functionCallsToCitations(functionCalls: FunctionCall[]): KeyValuePair[] {
  if (functionCalls.length === 0) return [];

  const citations = functionCalls.map((fc: FunctionCall & { response?: string }) => ({
    type: CitationType.TYPE_FUNCTION_CALL,
    title: fc.name,
    content: fc.response || '',
    contentSize: fc.response?.length || 0,
    contentSizeLimit: CITATION_CONTENT_SIZE_LIMIT,
    functionArguments: fc.arguments || '{}',
  }));

  return [{ key: 'citations', value: JSON.stringify(citations) }];
}
